import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  TextField,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from "@mui/material";
import { useState, useMemo } from "react";

export default function EstadisticasVentas({ state }) {
  const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const hoy = new Date().toLocaleDateString('en-CA', {
    timeZone: userTZ
  });
  
  const [inicio, setInicio] = useState(hoy);
  const [fin, setFin] = useState(hoy);

  const ventas = useMemo(
    () =>
      state.filtrarVentas(
        `${inicio}T00:00:00`,
        `${fin}T23:59:59`
      ),
    [inicio, fin, state]
  );

  const total = ventas.reduce((a, b) => a + b.total, 0);
  const ticketPromedio = ventas.length ? total / ventas.length : 0;

  /* =========================
     PRODUCTOS
  ========================== */

  const productos = {};
  const categorias = {};

  ventas.forEach(v =>
    v.items.forEach(i => {
      productos[i.nombre] = (productos[i.nombre] || 0) + i.cantidad;
      categorias[i.categoria] =
        (categorias[i.categoria] || 0) + i.subtotal;
    })
  );

  const rankingProductos = Object.entries(productos)
    .map(([nombre, cantidad]) => ({ nombre, cantidad }))
    .sort((a, b) => b.cantidad - a.cantidad);

  const rankingCategorias = Object.entries(categorias)
    .map(([categoria, total]) => ({ categoria, total }))
    .sort((a, b) => b.total - a.total);

  /* ========================= */

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Estadísticas de Ventas
      </Typography>

      {/* FILTROS */}
      <Stack direction="row" spacing={2} mb={3}>
        <TextField
          label="Desde"
          type="date"
          value={inicio}
          onChange={e => setInicio(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Hasta"
          type="date"
          value={fin}
          onChange={e => setFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      {/* RESUMEN */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2">Total Vendido</Typography>
              <Typography variant="h4">
                {state.config.moneda} {total.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2">Cantidad de Ventas</Typography>
              <Typography variant="h4">{ventas.length}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2">Ticket Promedio</Typography>
              <Typography variant="h4">
                {state.config.moneda} {ticketPromedio.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* PRODUCTOS */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6">Productos más vendidos</Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Cantidad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankingProductos.map((p, i) => (
                <TableRow key={i}>
                  <TableCell>{p.nombre}</TableCell>
                  <TableCell align="right">{p.cantidad}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* CATEGORÍAS */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6">Ventas por Categoría</Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Categoría</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankingCategorias.map((c, i) => (
                <TableRow key={i}>
                  <TableCell>{c.categoria}</TableCell>
                  <TableCell align="right">
                    {state.config.moneda} {c.total.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      <Typography
        variant="caption"
        color="text.secondary"
        textAlign="center"
        display="block"
      >
        Estadísticas calculadas desde el historial de ventas
      </Typography>
    </Box>
  );
}
